import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { OpenFeature } from '@openfeature/react-sdk';

/**
 * Kill switch for the WAV-writer failure recovery path. Declared here rather
 * than inline at its hook so the payload reader below and
 * `useIsAppsWavWriterRecoveryDesktopEnabled` cannot drift onto two different
 * keys.
 */
export const WAV_WRITER_RECOVERY_FLAG_KEY = 'release-apps-wav-writer-recovery-desktop';

/**
 * Imperative read of the recovery flag's raw Flagsmith payload, plumbed down
 * with the capture-start params and decoded on the Rust side
 * (`audio::record::stall_budget`) into the WAV-writer backpressure budget.
 *
 * The payload's one key is `wavWriterStallBudgetSecs`: how long an unbroken run
 * of writer-channel backpressure may last before the recording is torn down.
 * `0` disables the tolerance entirely and restores the fail-fast behaviour
 * this tolerance replaced. It is deliberately not parsed here — Rust owns the decode and its
 * safe fallbacks, so a missing, malformed or out-of-range value can only ever
 * leave the compiled 10s default in place.
 *
 * Unlike `getSelfHealingStopPayload`, this is NOT gated on the flag's boolean
 * value. The abort this budget governs is not flag-gated — the flag decides
 * only whether the user is told — so a payload that arrives while the boolean
 * is off is still meaningful and must not be dropped on the way down.
 *
 * A plain function rather than a hook: it is read inside `mutationFn` bodies
 * that cannot call a hook, and `OpenFeature.getClient` resolves against the
 * NoOp evaluation with no provider mounted, so it is safe in unit tests.
 */
export const getWavWriterRecoveryPayload = (): Record<string, unknown> | undefined => {
  const payload = OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getObjectValue(
    WAV_WRITER_RECOVERY_FLAG_KEY,
    {}
  );
  return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : undefined;
};
